import React, { useState, useEffect } from 'react';
import axios from 'axios';
import moment from 'moment';
import { API_URL } from "../../utils/constant";
import { useDispatch, useSelector } from "react-redux";
// import Pagination from '../../components/Pagination/Pagination';
import Pagination from "../Pagination/Pagination";
import Select from 'react-select';
import makeAnimated from 'react-select/animated';
import { toastr } from 'react-redux-toastr';
import { Link } from 'react-router-dom';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format, isValid } from "date-fns";

const animatedComponents = makeAnimated();

const ConsolidatedAssets = () => {
    const orgId = useSelector((state) => state?.summary?.localClient?._id || state?.summary?.clients?.[0]?._id);
    const [assets, setAssets] = useState([]);
    const [loading, setLoading] = useState(false);
    const [scanning, setScanning] = useState({});
    const [dateRange, setDateRange] = useState(null);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [datePickerOpen, setDatePickerOpen] = useState(false);
    const [tempStartDate, setTempStartDate] = useState(null);
    const [tempEndDate, setTempEndDate] = useState(null);
    const [startDateObj, setStartDateObj] = useState(null);
    const [endDateObj, setEndDateObj] = useState(null);
    const [tagSearch, setTagSearch] = useState('');
    const [severityFilter, setSeverityFilter] = useState('');
    const [targetFilter, setTargetFilter] = useState('');
    const [typeFilter, setTypeFilter] = useState('');
    const [scanStatusFilter, setScanStatusFilter] = useState('');
    const [availableTags, setAvailableTags] = useState([]);
    const [showTagSuggestions, setShowTagSuggestions] = useState(false);
    const [limit, setLimit] = useState(100);
    const [showAddTagModal, setShowAddTagModal] = useState(false);
    const [selectedAsset, setSelectedAsset] = useState(null);
    const [newTags, setNewTags] = useState([]);
    const [newTagInput, setNewTagInput] = useState('');
    const [summary, setSummary] = useState({
        total: 0,
        subdomains: 0,
        activeSubdomains: 0,
        danglingSubdomains: 0,
        high_severity: 0,
        medium_severity: 0,
        low_severity: 0,
        critical_severity: 0,
        unknown_severity: 0,
        info_severity: 0
    });
    const [specificOrgAssets, setSpecificOrgAssets] = useState(null);
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalRecords: 0,
        limit: 100
    });
    const [showAddAssetModal, setShowAddAssetModal] = useState(false);

    // Update startDateObj and endDateObj whenever startDate or endDate change
    useEffect(() => {
        if (startDate) {
            setStartDateObj(new Date(startDate));
        }
        if (endDate) {
            setEndDateObj(new Date(endDate));
        }
    }, [startDate, endDate]);

    // Update tempStartDate and tempEndDate whenever startDate or endDate change
    useEffect(() => {
        if (startDate) {
            setTempStartDate(new Date(startDate));
        }
        if (endDate) {
            setTempEndDate(new Date(endDate));
        }
    }, [startDate, endDate]);

    // Format the displayed date range
    const formatDateRange = () => {
        if (startDate && endDate) {
            return `Selected date: ${startDate ? format(new Date(startDate), 'MM/dd/yyyy') : ''} to ${endDate ? format(new Date(endDate), 'MM/dd/yyyy') : ''}`;
        } else if (startDate) {
            return `From: ${startDate ? format(new Date(startDate), 'MM/dd/yyyy') : ''}`;
        } else if (endDate) {
            return `Till: ${endDate ? format(new Date(endDate), 'MM/dd/yyyy') : ''}`;
        }
        return "Select date range"; // Default placeholder text
    };

    // Apply the selected date range
    const applyDateRange = () => {
        if (tempStartDate) {
            setStartDate(format(tempStartDate, 'yyyy-MM-dd'));
        } else {
            setStartDate('');
        }

        if (tempEndDate) {
            setEndDate(format(tempEndDate, 'yyyy-MM-dd'));
        } else {
            setEndDate('');
        }

        setDatePickerOpen(false);
        setPagination(prev => ({
            ...prev,
            currentPage: 1
        }));
    };

    // Cancel date selection and reset all dates
    const cancelDateSelection = () => {
        setTempStartDate(null);
        setTempEndDate(null);
        setStartDate('');
        setEndDate('');
        setStartDateObj(null);
        setEndDateObj(null);
        setDatePickerOpen(false);
    };
    const [assetType, setAssetType] = useState('subdomain');
    const [assetInput, setAssetInput] = useState('');
    const [addingAsset, setAddingAsset] = useState(false);
    // New Asset Tagging state
    const [newAssetTags, setNewAssetTags] = useState([]); // array of tag strings for assets being added
    const [newAssetTagInput, setNewAssetTagInput] = useState(''); // input text for creating a new tag while adding assets
    const [selectedAssets, setSelectedAssets] = useState([]);
    const [isBulkScanning, setIsBulkScanning] = useState(false);
    const [initiateActiveScan, setInitiateActiveScan] = useState(true);
    const [selectedTagsForScan, setSelectedTagsForScan] = useState([]);
    const [showTagScanModal, setShowTagScanModal] = useState(false);
    const [isTagScanning, setIsTagScanning] = useState(false);
    const [sortByDate, setSortByDate] = useState('desc');
    const [isExporting, setIsExporting] = useState(false);
    const [markingIncorrect, setMarkingIncorrect] = useState({});

    const fetchAssets = async (page = 1) => {
        try {
            setLoading(true);
            const params = {
                limit,
                page,
                tags: tagSearch,
                sortByDate
            };

            if (severityFilter) {
                params.severity = severityFilter;
            }

            if (targetFilter) {
                params.target = targetFilter;
            }

            if (typeFilter) {
                params.type = typeFilter;
            }

            if (scanStatusFilter) {
                params.scanStatus = scanStatusFilter;
            }

            if (startDate) {
                params.from = startDate;
            }

            if (endDate) {
                params.till = endDate;
            }

            const response = await axios.get(`${API_URL}/digital-inventory/${orgId}/combined-assets`, { params });

            if (response.data.error) {
                toastr.error('Error', response.data.userMessage || 'Failed to fetch assets');
                return;
            }

            const { assets, assetsStats } = response.data.data;

            // Transform the assets data to match component's expected structure
            const transformedAssets = assets.map(asset => ({
                _id: asset._id,
                value: asset.target,
                type: asset.type,
                ip: asset.type === 'ip' ? asset.target : null,
                scanStatus: asset.scanStatus,
                status: {
                    isActive: asset.scanStatus !== 'failed',
                    isDangling: asset.isDangling || false,
                    severity: {
                        critical: asset.totalCriticalVulns || 0,
                        high: asset.totalHighVulns || 0,
                        medium: asset.totalMediumVulns || 0,
                        low: asset.totalLowVulns || 0,
                        info: asset.totalInfoVulns || 0
                    }
                },
                tags: asset.tags || [],
                createdAt: asset.createdAt || new Date().toISOString(),
                lastScanDate: asset.lastScanDate || 0,
                activeVulnLastScanDate: asset.activeVulnLastScanDate || 0
            }));

            // Transform the summary stats
            const statsData = assetsStats[0] || {};
            const transformedSummary = {
                total: statsData.totalAssets || 0,
                subdomains: statsData.totalSubDomains || 0,
                activeSubdomains: statsData.totalSubDomains - (statsData.totalDanglingSubDomains || 0),
                danglingSubdomains: statsData.totalDanglingSubDomains || 0,
                high_severity: statsData.totalHighVulns || 0,
                medium_severity: statsData.totalMediumVulns || 0,
                low_severity: statsData.totalLowVulns || 0,
                critical_severity: statsData.totalCriticalVulns || 0,
                unknown_severity: statsData.totalInfoVulns || 0,
                info_severity: statsData.totalInfoVulns || 0,
                lastScanDate: statsData.lastScanDate || 0,
                activeVulnLastScanDate: statsData.activeVulnLastScanDate || 0
            };

            setAssets(transformedAssets);
            setSummary(transformedSummary);

            // Update pagination from response
            const paginationData = {
                currentPage: response.data.data.currentPage || 1,
                totalPages: response.data.data.totalPages || 1,
                totalRecords: statsData.totalAssets || 0,
                limit
            };

            setPagination(paginationData);
        } catch (error) {
            // console.error removed
            toastr.error('Error', error.response?.data?.userMessage || 'Failed to fetch assets');
        } finally {
            setLoading(false);
        }
    };

    const fetchAvailableTags = async (search = '') => {
        try {
            const response = await axios.get(`${API_URL}/digital-inventory/${orgId}/tags`, {
                params: { search }
            });
            setAvailableTags(response.data.data || []);
        } catch (error) {
            // console.error removed
        }
    };

    useEffect(() => {
        if (orgId) {
            fetchAssets(1, orgId);
            fetchAvailableTags();
        }
    }, [tagSearch, limit, startDate, endDate, orgId, severityFilter, targetFilter, typeFilter, scanStatusFilter, sortByDate]);

    const handlePageChange = (page) => {
        fetchAssets(page);
    };

    const handleAddTag = async () => {
        if (!newTags.length) return;

        try {
            const config = {
                headers: {
                    'Content-Type': 'application/json',
                }
            };

            // If multiple assets are selected, use the selectedAssets array
            const assetIds = selectedAssets.length > 0 ? selectedAssets.map(asset => asset._id) : [selectedAsset._id];

            await axios.post(`${API_URL}/digital-inventory/${orgId}/asset/tag`, {
                assetIds,
                tags: newTags
            }, config);

            setShowAddTagModal(false);
            setNewTags([]);
            setNewTagInput('');
            fetchAssets(pagination.currentPage);
            toastr.success('Success', 'Tags added successfully');
        } catch (error) {
            // console.error removed
            toastr.error('Error', error.response?.data?.message || 'Failed to add tags');
        }
    };

    const handleDeleteTag = async (assetId, tag) => {
        try {
            const config = {
                headers: {
                    'Content-Type': 'application/json',
                },
                data: {
                    assetId: [assetId],
                    tags: [tag]
                }
            };

            await axios.delete(`${API_URL}/digital-inventory/${orgId}/asset/tag`, config);
            toastr.success('Success', 'Tag removed successfully');
            fetchAssets(pagination.currentPage);
        } catch (error) {
            // console.error removed
            toastr.error('Error', error.response?.data?.message || 'Failed to remove tag');
        }
    };



    const handleSetDateRange = () => {
        setDateRange({
            from: moment(),
            till: moment().add(1, 'month')
        });
    };

    const handleClearDateRange = () => {
        setDateRange(null);
    };

    const handleScan = async (assetId, assetValue, assetType, tags = []) => {
        // Set scanning state for this specific asset only
        setScanning(prev => ({ ...prev, [assetId]: true }));

        try {
            const payload = {
                targets: assetValue === 'all' ? 'all' : [assetValue]
            };

            if (tags && tags.length > 0) {
                payload.targetType = "tags";
            } else {
                payload.targetType = "assets";
            }

            const response = await axios.post(`${API_URL}/create-active-scan/${orgId}`, payload);

            // Update the specific asset's scan status in the assets array
            setAssets(prevAssets => prevAssets.map(asset => {
                if (asset._id === assetId) {
                    return {
                        ...asset,
                        scanStatus: 'pending'
                    };
                }
                return asset;
            }));

            toastr.success('Success', response.data.message);
        } catch (error) {
            // console.error removed
            toastr.error('Error', error.response?.data?.message || 'Failed to initiate scan');

            // Reset scanning state for this asset in case of error
            setScanning(prev => ({ ...prev, [assetId]: false }));
        }
    };

    const handleMarkAsIncorrectIp = async (assetId, ip) => {
        // Set marking state for this specific asset
        setMarkingIncorrect(prev => ({ ...prev, [assetId]: true }));

        try {
            const params = new URLSearchParams({
                page: pagination.currentPage,
                category: 'all',
                port: '',
                country: '',
                cloudprovider: '',
                ipq: ip,
                highestSeverity: '',
                sortByDate: sortByDate,
                domain: ''
            });

            const payload = {
                action: 'markAsIncorrectIp',
                ips: ip,
                actionNote: ''
            };

            const response = await axios.post(
                `${API_URL}/digital-inventory/${orgId}/action?${params.toString()}`,
                payload,
                {
                    headers: {
                        'Content-Type': 'application/json'
                    }
                }
            );

            toastr.success('Success', response.data.message || 'IP marked as incorrect successfully');

            // Refresh the assets list
            fetchAssets(pagination.currentPage);
        } catch (error) {
            // console.error removed
            toastr.error('Error', error.response?.data?.message || 'Failed to mark IP as incorrect');
        } finally {
            // Reset marking state for this asset
            setMarkingIncorrect(prev => ({ ...prev, [assetId]: false }));
        }
    };

    const handleAddAsset = async () => {
        if (!assetInput.trim()) return;

        setAddingAsset(true);
        try {
            const assets = assetInput.split('\n').map(item => item.trim()).filter(item => item);
            // Build payload with optional tags if provided
            const basePayload = {
                initiateActiveScan
            };
            if (newAssetTags.length > 0) {
                basePayload.tags = newAssetTags; // optimistic assumption backend supports tags on creation
            }

            if (assetType === 'subdomain') {
                await axios.post(`${API_URL}/${orgId}/queue/subdomain/add`, {
                    ...basePayload,
                    subDomains: assets
                });
            } else {
                await axios.post(`${API_URL}/${orgId}/queue/ipdiscovery/add`, {
                    ...basePayload,
                    ips: assets
                });
            }

            toastr.success('Success', `Assets added successfully${newAssetTags.length ? ` with tags: ${newAssetTags.join(', ')}` : ''}`);
            setShowAddAssetModal(false);
            setAssetInput('');
            setNewAssetTags([]);
            setNewAssetTagInput('');
            fetchAssets(pagination.currentPage);
        } catch (error) {
            // console.error removed
            toastr.error('Error', error.response?.data?.message || 'Failed to add assets');
        } finally {
            setAddingAsset(false);
        }
    };

    const tagOptions = availableTags.map(tag => ({
        value: tag,
        label: tag
    }));

    const severityOptions = [
        { value: '', label: 'All Severities' },
        { value: 'critical', label: 'Critical' },
        { value: 'high', label: 'High' },
        { value: 'medium', label: 'Medium' },
        { value: 'low', label: 'Low' },
        { value: 'info', label: 'Info' }
    ];

    const scanStatusOptions = [
        { value: '', label: 'All Scan Status' },
        { value: 'pending', label: 'Pending' },
        { value: 'completed', label: 'Completed' },
        { value: 'scanning', label: 'Scanning' },
        { value: 'unscanned', label: 'Unscanned' }
    ];

    const handleBulkScan = async () => {
        if (selectedAssets.length === 0) return;

        // Filter out assets that are already pending
        const assetsToScan = selectedAssets.filter(asset => asset.scanStatus !== 'pending');

        if (assetsToScan.length === 0) {
            toastr.warning('Warning', 'All selected assets are already being scanned');
            return;
        }

        setIsBulkScanning(true);
        try {
            const targets = assetsToScan.map(asset => asset.value);
            const response = await axios.post(`${API_URL}/create-active-scan/${orgId}`, {
                targetType: "assets",
                targets
            });

            // Update the scan status for the scanned assets
            setAssets(prevAssets => prevAssets.map(asset => {
                if (assetsToScan.some(scanAsset => scanAsset._id === asset._id)) {
                    return {
                        ...asset,
                        scanStatus: 'pending'
                    };
                }
                return asset;
            }));

            toastr.success('Success', `Started scanning ${assetsToScan.length} assets`);
            setSelectedAssets([]); // Clear selection after successful scan
        } catch (error) {
            // console.error removed
            toastr.error('Error', error.response?.data?.message || 'Failed to initiate bulk scan');
        } finally {
            setIsBulkScanning(false);
        }
    };

    const handleTagBasedScan = async () => {
        if (selectedTagsForScan.length === 0) return;

        setIsTagScanning(true);
        try {
            const response = await axios.post(`${API_URL}/create-active-scan/${orgId}`, {
                targetType: "tags",
                targets: selectedTagsForScan
            });

            toastr.success('Success', `Started scanning assets with tags: ${selectedTagsForScan.join(', ')}`);
            setShowTagScanModal(false);
            setSelectedTagsForScan([]);

            // Refresh the assets list to show updated scan statuses
            fetchAssets(pagination.currentPage);
        } catch (error) {
            // console.error removed
            toastr.error('Error', error.response?.data?.message || 'Failed to initiate tag-based scan');
        } finally {
            setIsTagScanning(false);
        }
    };

    const handleSelectAllAssets = (e) => {
        if (e.target.checked) {
            // Only select assets that are not in pending state
            const nonPendingAssets = assets.filter(asset => asset.scanStatus !== 'pending');
            setSelectedAssets(nonPendingAssets);
        } else {
            setSelectedAssets([]);
        }
    };

    const handleSelectAsset = (asset) => {
        // Don't allow selection of pending assets
        if (asset.scanStatus === 'pending') {
            toastr.warning('Warning', 'Cannot select an asset that is already being scanned');
            return;
        }

        if (selectedAssets.find(a => a._id === asset._id)) {
            setSelectedAssets(selectedAssets.filter(a => a._id !== asset._id));
        } else {
            setSelectedAssets([...selectedAssets, asset]);
        }
    };

    const handleExport = async () => {
        setIsExporting(true);
        try {
            const params = new URLSearchParams();

            if (severityFilter) {
                params.append('severity', severityFilter);
            }
            if (targetFilter) {
                params.append('target', targetFilter);
            }
            if (typeFilter) {
                params.append('type', typeFilter);
            }
            if (scanStatusFilter) {
                params.append('scanStatus', scanStatusFilter);
            }
            if (startDate) {
                params.append('from', startDate);
            }
            if (endDate) {
                params.append('till', endDate);
            }
            if (tagSearch) {
                params.append('tags', tagSearch);
            }
            if (sortByDate) {
                params.append('sortByDate', sortByDate);
            }

            const response = await axios.get(`${API_URL}/digital-inventory/${orgId}/combined-assets/export?${params.toString()}`, {
                responseType: 'blob'
            });

            // Create blob link to download
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;

            // Generate filename with current date and filters
            const now = new Date();
            const dateStr = now.toISOString().split('T')[0];
            const filterStr = severityFilter ? `_${severityFilter}` : '';
            const typeStr = typeFilter ? `_${typeFilter}` : '';
            link.setAttribute('download', `consolidated_assets${filterStr}${typeStr}_${dateStr}.xlsx`);

            // Append to html link element page
            document.body.appendChild(link);

            // Start download
            link.click();

            // Clean up and remove the link
            link.remove();
            window.URL.revokeObjectURL(url);

            toastr.success('Success', 'Export completed successfully');
        } catch (error) {
            toastr.error('Error', error.response?.data?.message || 'Failed to export assets');
        } finally {
            setIsExporting(false);
        }
    };

    // Check if any filters are applied
    const hasActiveFilters = () => {
        return severityFilter || targetFilter || typeFilter || scanStatusFilter ||
            startDate || endDate || tagSearch || sortByDate !== 'desc';
    };



    return (
        <div style={{ padding: '1.5rem' }}>
            <div style={{
                background: 'white',
                borderRadius: '8px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                marginBottom: '1.5rem'
            }}>
                <div style={{
                    padding: '1.5rem',
                    borderBottom: '1px solid #eee',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <div>
                        <h3 style={{
                            margin: 0,
                            color: '#2c3e50',
                            fontSize: '1.5rem',
                            fontWeight: '600'
                        }}>Consolidated Assets</h3>
                        <p style={{
                            margin: '0.5rem 0 0',
                            color: '#6c757d',
                            fontSize: '0.9rem'
                        }}>Monitor and manage your digital assets</p>
                    </div>
                    <div style={{ display: 'flex', gap: '1rem' }}>
                        <button
                            style={{
                                background: '#08283f',
                                color: 'white',
                                border: 'none',
                                padding: '0.5rem 1rem',
                                borderRadius: '6px',
                                display: 'flex',
                                alignItems: 'center',
                                cursor: 'pointer',
                                transition: 'all 0.2s'
                            }}
                            onClick={() => setShowAddAssetModal(true)}
                        >
                            <i className="fa fa-plus mr-2"></i>
                            Add Asset
                        </button>
                        <button
                            style={{
                                background: '#28a745',
                                color: 'white',
                                border: 'none',
                                padding: '0.5rem 1rem',
                                borderRadius: '6px',
                                display: 'flex',
                                alignItems: 'center',
                                cursor: 'pointer',
                                transition: 'all 0.2s'
                            }}
                            onClick={() => setShowTagScanModal(true)}
                        >
                            <i className="fa fa-tags mr-2"></i>
                            Tag-Based Scan
                        </button>
                        <Link
                            to="/active-scan/schedule"
                            style={{
                                background: '#08283f',
                                color: 'white',
                                border: 'none',
                                padding: '0.5rem 1rem',
                                borderRadius: '6px',
                                display: 'flex',
                                alignItems: 'center',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                textDecoration: 'none'
                            }}
                        >
                            <i className="fa fa-clock mr-2"></i>
                            Schedule Scan
                        </Link>
                    </div>
                </div>
                <div style={{ padding: '1.5rem' }}>
                    {summary && (
                        <div style={{ marginBottom: '1.5rem' }}>
                            <div style={{
                                background: '#f8f9fa',
                                borderRadius: '8px',
                                padding: '1.5rem'
                            }}>
                                <h5 style={{
                                    marginBottom: '1rem',
                                    color: '#2c3e50',
                                    fontWeight: '600'
                                }}>Asset Overview</h5>
                                <div style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: '1rem'
                                }}>
                                    <div style={{
                                        flex: '1',
                                        minWidth: '200px',
                                        background: 'white',
                                        borderRadius: '8px',
                                        padding: '1rem',
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                                    }}>
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center'
                                        }}>
                                            <div style={{
                                                background: '#08283f',
                                                color: 'white',
                                                borderRadius: '50%',
                                                width: '48px',
                                                height: '48px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                marginRight: '1rem'
                                            }}>
                                                <i className="fa fa-server"></i>
                                            </div>
                                            <div>
                                                <span style={{
                                                    display: 'block',
                                                    fontSize: '1.5rem',
                                                    fontWeight: '600',
                                                    color: '#2c3e50'
                                                }}>{summary.total}</span>
                                                <span style={{
                                                    color: '#6c757d',
                                                    fontSize: '0.9rem'
                                                }}>Total Assets</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div style={{
                                        flex: '1',
                                        minWidth: '200px',
                                        background: 'white',
                                        borderRadius: '8px',
                                        padding: '1rem',
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                                    }}>
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center'
                                        }}>
                                            <div style={{
                                                background: '#17a2b8',
                                                color: 'white',
                                                borderRadius: '50%',
                                                width: '48px',
                                                height: '48px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                marginRight: '1rem'
                                            }}>
                                                <i className="fa fa-globe"></i>
                                            </div>
                                            <div>
                                                <span style={{
                                                    display: 'block',
                                                    fontSize: '1.5rem',
                                                    fontWeight: '600',
                                                    color: '#2c3e50'
                                                }}>{summary.subdomains}</span>
                                                <span style={{
                                                    color: '#6c757d',
                                                    fontSize: '0.9rem'
                                                }}>Subdomains</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div style={{
                                        flex: '1',
                                        minWidth: '200px',
                                        background: 'white',
                                        borderRadius: '8px',
                                        padding: '1rem',
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                                    }}>
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center'
                                        }}>
                                            <div style={{
                                                background: '#28a745',
                                                color: 'white',
                                                borderRadius: '50%',
                                                width: '48px',
                                                height: '48px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                marginRight: '1rem'
                                            }}>
                                                <i className="fa fa-network-wired"></i>
                                            </div>
                                            <div>
                                                <span style={{
                                                    display: 'block',
                                                    fontSize: '1.5rem',
                                                    fontWeight: '600',
                                                    color: '#2c3e50'
                                                }}>{summary.total - summary.activeSubdomains}</span>
                                                <span style={{
                                                    color: '#6c757d',
                                                    fontSize: '0.9rem'
                                                }}>IP Addresses</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                    <div style={{ marginBottom: '1.5rem' }}>
                        <div style={{
                            background: 'white',
                            borderRadius: '8px',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                        }}>
                            <div style={{
                                padding: '0.75rem 1rem',
                                borderBottom: '1px solid #eee',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem'
                            }}>
                                <i className="fa fa-shield-alt" style={{ color: '#08283f' }}></i>
                                <h5 style={{
                                    margin: 0,
                                    color: '#2c3e50',
                                    fontWeight: '600',
                                    fontSize: '1rem'
                                }}>Vulnerability Severity Overview</h5>
                            </div>
                            <div style={{ padding: '1rem' }}>
                                <div style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: '0.5rem'
                                }}>
                                    {[
                                        { label: 'Critical', value: summary.critical_severity, color: '#dc3545', icon: 'exclamation-triangle' },
                                        { label: 'High', value: summary.high_severity, color: '#ffc107', icon: 'exclamation-circle' },
                                        { label: 'Medium', value: summary.medium_severity, color: '#17a2b8', icon: 'info-circle' },
                                        { label: 'Low', value: summary.low_severity, color: '#28a745', icon: 'check-circle' },
                                        { label: 'Info', value: summary.info_severity, color: '#6c757d', icon: 'info' }
                                    ].map((severity, index) => (
                                        <Link
                                            key={severity.label}
                                            to={`/active-scan/issue?severity=${severity.label.toLowerCase()}`}
                                            style={{
                                                flex: '1',
                                                minWidth: '120px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '0.75rem',
                                                padding: '0.75rem',
                                                background: `${severity.color}10`,
                                                borderRadius: '6px',
                                                border: `1px solid ${severity.color}30`,
                                                textDecoration: 'none',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s ease',
                                                '&:hover': {
                                                    transform: 'translateY(-2px)',
                                                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                                                }
                                            }}
                                        >
                                            <div style={{
                                                background: severity.color,
                                                color: 'white',
                                                borderRadius: '50%',
                                                width: '32px',
                                                height: '32px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                flexShrink: 0
                                            }}>
                                                <i className={`fa fa-${severity.icon}`}></i>
                                            </div>
                                            <div>
                                                <span style={{
                                                    display: 'block',
                                                    fontSize: '1.25rem',
                                                    fontWeight: '600',
                                                    color: severity.color,
                                                    lineHeight: '1.2'
                                                }}>{severity.value}</span>
                                                <span style={{
                                                    color: '#6c757d',
                                                    fontSize: '0.875rem'
                                                }}>{severity.label}</span>
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div style={{ marginBottom: '1.5rem' }}>
                        <div style={{
                            background: 'white',
                            borderRadius: '8px',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                        }}>
                            <div style={{
                                padding: '1rem 1.5rem',
                                borderBottom: '1px solid #eee'
                            }}>
                                <h5 style={{
                                    margin: 0,
                                    color: '#2c3e50',
                                    fontWeight: '600'
                                }}>Filters</h5>
                            </div>
                            <div className="card-body p-4">
                                <div className="row">
                                    {/* Export Button - Always available */}
                                    <div className="col-12 mb-3">
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'flex-end',
                                            alignItems: 'center',
                                            gap: '1rem',
                                            padding: '0.75rem',
                                            background: hasActiveFilters() ? '#f8f9fa' : '#e3f2fd',
                                            borderRadius: '6px',
                                            border: hasActiveFilters() ? '1px solid #e9ecef' : '1px solid #bbdefb'
                                        }}>
                                            <span style={{
                                                color: hasActiveFilters() ? '#6c757d' : '#1976d2',
                                                fontSize: '0.9rem',
                                                fontWeight: '500'
                                            }}>
                                                {hasActiveFilters() ? 'Filters applied - Export available' : 'Export all data'}
                                            </span>
                                            <button
                                                style={{
                                                    background: hasActiveFilters() ? '#28a745' : '#1976d2',
                                                    color: 'white',
                                                    border: 'none',
                                                    padding: '0.5rem 1rem',
                                                    borderRadius: '6px',
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '0.5rem',
                                                    transition: 'all 0.2s',
                                                    opacity: isExporting ? 0.7 : 1
                                                }}
                                                onClick={handleExport}

                                            >
                                                {isExporting && (
                                                    <div style={{
                                                        width: '1rem',
                                                        height: '1rem',
                                                        border: '2px solid #fff',
                                                        borderTop: '2px solid transparent',
                                                        borderRadius: '50%',
                                                        animation: 'spin 1s linear infinite',
                                                        marginRight: '0.5rem',
                                                        display: 'inline-block'
                                                    }}></div>
                                                )}
                                                <i className="fa fa-download"></i>
                                                {isExporting ? 'Exporting...' : (hasActiveFilters() ? 'Export Filtered Data' : 'Export All Data')}
                                            </button>
                                        </div>
                                    </div>
                                    {/* Date Range */}
                                    <div className="col-md-4 col-lg-4 mb-3">
                                        <div className="form-group">
                                            <label className="form-label font-weight-bold">Date Range</label>
                                            <div className="date-picker-container">
                                                <div className="date-range-input position-relative">
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        placeholder="Select date range"
                                                        value={formatDateRange()}
                                                        onClick={() => setDatePickerOpen(true)}
                                                        readOnly
                                                        style={{
                                                            cursor: 'pointer',
                                                            backgroundColor: '#fff'
                                                        }}
                                                    />
                                                    <span className="calendar-icon">
                                                        <i className="fa fa-calendar" aria-hidden="true"></i>
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    {/* Search by Tag */}
                                    <div className="col-md-4 col-lg-4 mb-3">
                                        <div className="form-group">
                                            <label className="form-label font-weight-bold">Search by Tag</label>
                                            <Select
                                                components={animatedComponents}
                                                options={tagOptions}
                                                value={tagOptions.find(option => option.value === tagSearch)}
                                                onChange={(selected) => setTagSearch(selected ? selected.value : '')}
                                                placeholder="Select Tag"
                                                isClearable
                                                styles={{
                                                    control: (base) => ({
                                                        ...base,
                                                        border: '1px solid #ddd',
                                                        borderRadius: '6px',
                                                        boxShadow: 'none',
                                                        '&:hover': {
                                                            borderColor: '#453acd'
                                                        }
                                                    })
                                                }}
                                            />
                                        </div>
                                    </div>
                                    {/* Severity Filter */}
                                    <div className="col-md-4 col-lg-4 mb-3">
                                        <div className="form-group">
                                            <label className="form-label font-weight-bold">Filter by Severity</label>
                                            <Select
                                                options={severityOptions}
                                                value={severityOptions.find(option => option.value === severityFilter)}
                                                onChange={(selected) => setSeverityFilter(selected ? selected.value : '')}
                                                placeholder="Select Severity"
                                                isClearable
                                                styles={{
                                                    control: (base) => ({
                                                        ...base,
                                                        border: '1px solid #ddd',
                                                        borderRadius: '6px',
                                                        boxShadow: 'none',
                                                        '&:hover': {
                                                            borderColor: '#453acd'
                                                        }
                                                    })
                                                }}
                                            />
                                        </div>
                                    </div>
                                    {/* Target Filter */}
                                    <div className="col-md-4 col-lg-4 mb-3">
                                        <div className="form-group">
                                            <label className="form-label font-weight-bold">Target</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={targetFilter}
                                                onChange={(e) => setTargetFilter(e.target.value)}
                                                placeholder="Enter target"
                                            />
                                        </div>
                                    </div>
                                    {/* Type Filter (if any) */}
                                    <div className="col-md-4 col-lg-4 mb-3">
                                        <div className="form-group">
                                            <label className="form-label font-weight-bold">Type</label>
                                            <select
                                                className="form-control custom-select"
                                                value={typeFilter}
                                                onChange={(e) => setTypeFilter(e.target.value)}
                                            >
                                                <option value="">All Types</option>
                                                <option value="ip">IP Address</option>
                                                <option value="subdomain">Subdomain</option>
                                            </select>
                                        </div>
                                    </div>
                                    {/* Scan Status Filter */}
                                    <div className="col-md-4 col-lg-4 mb-3">
                                        <div className="form-group">
                                            <label className="form-label font-weight-bold">Filter by Scan Status</label>
                                            <Select
                                                options={scanStatusOptions}
                                                value={scanStatusOptions.find(option => option.value === scanStatusFilter)}
                                                onChange={(selected) => setScanStatusFilter(selected ? selected.value : '')}
                                                placeholder="Select Scan Status"
                                                isClearable
                                                styles={{
                                                    control: (base) => ({
                                                        ...base,
                                                        border: '1px solid #ddd',
                                                        borderRadius: '6px',
                                                        boxShadow: 'none',
                                                        '&:hover': {
                                                            borderColor: '#453acd'
                                                        }
                                                    })
                                                }}
                                            />
                                        </div>
                                    </div>
                                    {/* Sort by Date */}
                                    <div className="col-md-4 col-lg-4 mb-3">
                                        <div className="form-group">
                                            <label className="form-label font-weight-bold">Sort by Date</label>
                                            <select
                                                className="form-control custom-select"
                                                value={sortByDate}
                                                onChange={(e) => setSortByDate(e.target.value)}
                                            >
                                                <option value="desc">Newest First</option>
                                                <option value="asc">Oldest First</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div style={{
                        background: 'white',
                        borderRadius: '8px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                    }}>
                        <div style={{
                            padding: '1rem 1.5rem',
                            borderBottom: '1px solid #eee',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <h5 style={{
                                margin: 0,
                                color: '#2c3e50',
                                fontWeight: '600'
                            }}>Asset List</h5>
                            {selectedAssets.length > 0 && (
                                <div style={{ display: 'flex', gap: '1rem' }}>
                                    <button
                                        style={{
                                            background: '#08283f',
                                            color: '#fff',
                                            border: 'none',
                                            padding: '0.5rem 1rem',
                                            borderRadius: '6px',
                                            cursor: 'pointer',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '0.5rem',
                                            transition: 'all 0.2s',
                                            opacity: isBulkScanning ? 0.7 : 1
                                        }}
                                        onClick={handleBulkScan}
                                        disabled={isBulkScanning}
                                    >
                                        {isBulkScanning ? (
                                            <>
                                                <div style={{
                                                    width: '1rem',
                                                    height: '1rem',
                                                    border: '2px solid #fff',
                                                    borderTop: '2px solid transparent',
                                                    borderRadius: '50%',
                                                    animation: 'spin 1s linear infinite'
                                                }}></div>
                                                Scanning {selectedAssets.length} Assets...
                                            </>
                                        ) : (
                                            <>
                                                <i className="fa fa-search"></i>
                                                Scan {selectedAssets.length} Selected Assets
                                            </>
                                        )}
                                    </button>
                                    <button
                                        style={{
                                            background: '#28a745',
                                            color: '#fff',
                                            border: 'none',
                                            padding: '0.5rem 1rem',
                                            borderRadius: '6px',
                                            cursor: 'pointer',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '0.5rem',
                                            transition: 'all 0.2s'
                                        }}
                                        onClick={() => setShowAddTagModal(true)}
                                    >
                                        <i className="fa fa-tags"></i>
                                        Tag {selectedAssets.length} Selected Assets
                                    </button>
                                </div>
                            )}
                        </div>
                        <div style={{ padding: 0 }}>
                            {loading ? (
                                <div style={{
                                    textAlign: 'center',
                                    padding: '3rem'
                                }}>
                                    <div style={{
                                        width: '3rem',
                                        height: '3rem',
                                        border: '3px solid #f3f3f3',
                                        borderTop: '3px solid #08283f',
                                        borderRadius: '50%',
                                        margin: '0 auto 1rem',
                                        animation: 'spin 1s linear infinite'
                                    }}></div>
                                    <p style={{
                                        color: '#6c757d',
                                        margin: 0
                                    }}>Loading assets...</p>
                                </div>
                            ) : (
                                <div style={{ overflowX: 'auto' }}>
                                    <table style={{
                                        width: '100%',
                                        borderCollapse: 'collapse'
                                    }}>
                                        <thead>
                                            <tr style={{
                                                background: '#f8f9fa',
                                                borderBottom: '2px solid #eee'
                                            }}>
                                                <th style={{
                                                    padding: '1rem',
                                                    textAlign: 'left',
                                                    color: '#2c3e50',
                                                    fontWeight: '600'
                                                }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedAssets.length === assets.length && assets.length > 0}
                                                        onChange={handleSelectAllAssets}
                                                        style={{ cursor: 'pointer' }}
                                                    />
                                                </th>
                                                <th style={{
                                                    padding: '1rem',
                                                    textAlign: 'left',
                                                    color: '#2c3e50',
                                                    fontWeight: '600'
                                                }}>Asset Name</th>
                                                <th style={{
                                                    padding: '1rem',
                                                    textAlign: 'left',
                                                    color: '#2c3e50',
                                                    fontWeight: '600'
                                                }}>Type</th>

                                                <th style={{
                                                    padding: '1rem',
                                                    textAlign: 'left',
                                                    color: '#2c3e50',
                                                    fontWeight: '600'
                                                }}>Status</th>
                                                <th style={{
                                                    padding: '1rem',
                                                    textAlign: 'left',
                                                    color: '#2c3e50',
                                                    fontWeight: '600'
                                                }}>Tags</th>
                                                <th style={{
                                                    padding: '1rem',
                                                    textAlign: 'left',
                                                    color: '#2c3e50',
                                                    fontWeight: '600'
                                                }}>Vulnerabilities</th>
                                                <th style={{
                                                    padding: '1rem',
                                                    textAlign: 'left',
                                                    color: '#2c3e50',
                                                    fontWeight: '600'
                                                }}>Last Scan Date</th>
                                                <th style={{
                                                    padding: '1rem',
                                                    textAlign: 'left',
                                                    color: '#2c3e50',
                                                    fontWeight: '600'
                                                }}>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {assets.map(asset => (
                                                <tr key={asset?._id || asset?.value} style={{
                                                    borderBottom: '1px solid #eee',
                                                    background: selectedAssets.find(a => a._id === asset._id) ? '#f8f9fa' : 'transparent',
                                                    '&:hover': {
                                                        background: '#f8f9fa'
                                                    }
                                                }}>
                                                    <td style={{ padding: '1rem' }}>
                                                        <input
                                                            type="checkbox"
                                                            checked={!!selectedAssets.find(a => a._id === asset._id)}
                                                            onChange={() => handleSelectAsset(asset)}
                                                            style={{ cursor: 'pointer' }}
                                                        />
                                                    </td>
                                                    <td style={{ padding: '1rem' }}>
                                                        <Link to={`/consolidated-assets/${asset?.value}`}>{asset?.value}</Link>
                                                    </td>
                                                    <td style={{ padding: '1rem' }}>
                                                        <span style={{
                                                            background: '#f8f9fa',
                                                            padding: '0.25rem 0.5rem',
                                                            borderRadius: '4px',
                                                            fontSize: '0.875rem',
                                                            color: '#2c3e50'
                                                        }}>
                                                            {asset?.type === 'ip' ? 'IP Address' : asset?.type}
                                                        </span>
                                                    </td>
                                                    <td style={{ padding: '1rem' }}>
                                                        {asset.scanStatus === 'pending' && (
                                                            <span style={{
                                                                background: '#ffc107',
                                                                color: '#000',
                                                                padding: '0.25rem 0.5rem',
                                                                borderRadius: '4px',
                                                                fontSize: '0.875rem',
                                                                marginRight: '0.5rem'
                                                            }}>Pending</span>
                                                        )}
                                                        {asset.scanStatus === 'failed' && (
                                                            <span style={{
                                                                background: '#dc3545',
                                                                color: '#fff',
                                                                padding: '0.25rem 0.5rem',
                                                                borderRadius: '4px',
                                                                fontSize: '0.875rem',
                                                                marginRight: '0.5rem'
                                                            }}>Failed</span>
                                                        )}
                                                        {asset.scanStatus === 'completed' && (
                                                            <span style={{
                                                                background: '#28a745',
                                                                color: '#fff',
                                                                padding: '0.25rem 0.5rem',
                                                                borderRadius: '4px',
                                                                fontSize: '0.875rem',
                                                                marginRight: '0.5rem'
                                                            }}>Completed</span>
                                                        )}
                                                        {asset.scanStatus === 'scanning' && (
                                                            <span style={{
                                                                background: '#ffc107',
                                                                color: '#000',
                                                                padding: '0.25rem 0.5rem',
                                                                borderRadius: '4px',
                                                                fontSize: '0.875rem',
                                                                marginRight: '0.5rem'
                                                            }}>Scanning</span>
                                                        )}
                                                        {asset.status.isDangling && (
                                                            <span style={{
                                                                background: '#dc3545',
                                                                color: '#fff',
                                                                padding: '0.25rem 0.5rem',
                                                                borderRadius: '4px',
                                                                fontSize: '0.875rem'
                                                            }}>Dangling</span>
                                                        )}
                                                    </td>
                                                    <td style={{ padding: '1rem' }}>
                                                        <div style={{
                                                            display: 'flex',
                                                            flexWrap: 'wrap',
                                                            gap: '0.5rem',
                                                            alignItems: 'center'
                                                        }}>
                                                            {asset.tags && asset.tags.length > 0 ? (
                                                                asset.tags.map(tag => (
                                                                    tag.name && (
                                                                        <span key={tag.name} style={{
                                                                            background: '#08283f',
                                                                            color: '#fff',
                                                                            padding: '0.25rem 0.5rem',
                                                                            borderRadius: '4px',
                                                                            fontSize: '0.875rem',
                                                                            display: 'inline-flex',
                                                                            alignItems: 'center'
                                                                        }}>
                                                                            {tag.name}
                                                                            <i
                                                                                className="fa fa-times ml-1"
                                                                                style={{
                                                                                    cursor: 'pointer',
                                                                                    marginLeft: '0.25rem'
                                                                                }}
                                                                                onClick={() => handleDeleteTag(asset._id, tag.name)}
                                                                            />
                                                                        </span>
                                                                    )
                                                                ))
                                                            ) : (
                                                                <span style={{
                                                                    color: '#6c757d',
                                                                    fontSize: '0.875rem'
                                                                }}>No tags</span>
                                                            )}
                                                            <button
                                                                style={{
                                                                    background: 'white',
                                                                    border: '1px solid #08283f',
                                                                    color: '#08283f',
                                                                    padding: '0.25rem 0.5rem',
                                                                    borderRadius: '4px',
                                                                    cursor: 'pointer',
                                                                    display: 'inline-flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    minWidth: '32px'
                                                                }}
                                                                onClick={() => {
                                                                    if (selectedAssets.length > 0) {
                                                                        setShowAddTagModal(true);
                                                                    } else {
                                                                        setSelectedAsset(asset);
                                                                        setNewTags([]);
                                                                        setShowAddTagModal(true);
                                                                    }
                                                                }}
                                                            >
                                                                <i className="fa fa-plus"></i>
                                                            </button>
                                                        </div>
                                                    </td>
                                                    <td style={{ padding: '1rem' }}>
                                                        <div style={{
                                                            display: 'flex',
                                                            flexWrap: 'wrap',
                                                            gap: '0.5rem'
                                                        }}>
                                                            {asset.status.severity.critical > 0 && (
                                                                <span style={{
                                                                    background: '#dc3545',
                                                                    color: '#fff',
                                                                    padding: '0.25rem 0.5rem',
                                                                    borderRadius: '4px',
                                                                    fontSize: '0.875rem'
                                                                }}>
                                                                    Critical: {asset.status.severity.critical}
                                                                </span>
                                                            )}
                                                            {asset.status.severity.high > 0 && (
                                                                <span style={{
                                                                    background: '#ffc107',
                                                                    color: '#000',
                                                                    padding: '0.25rem 0.5rem',
                                                                    borderRadius: '4px',
                                                                    fontSize: '0.875rem'
                                                                }}>
                                                                    High: {asset.status.severity.high}
                                                                </span>
                                                            )}
                                                            {asset.status.severity.medium > 0 && (
                                                                <span style={{
                                                                    background: '#17a2b8',
                                                                    color: '#fff',
                                                                    padding: '0.25rem 0.5rem',
                                                                    borderRadius: '4px',
                                                                    fontSize: '0.875rem'
                                                                }}>
                                                                    Medium: {asset.status.severity.medium}
                                                                </span>
                                                            )}
                                                            {asset.status.severity.low > 0 && (
                                                                <span style={{
                                                                    background: '#28a745',
                                                                    color: '#fff',
                                                                    padding: '0.25rem 0.5rem',
                                                                    borderRadius: '4px',
                                                                    fontSize: '0.875rem'
                                                                }}>
                                                                    Low: {asset.status.severity.low}
                                                                </span>
                                                            )}
                                                            {asset.status.severity.unknown > 0 && (
                                                                <span style={{
                                                                    background: '#6c757d',
                                                                    color: '#fff',
                                                                    padding: '0.25rem 0.5rem',
                                                                    borderRadius: '4px',
                                                                    fontSize: '0.875rem'
                                                                }}>
                                                                    Unknown: {asset.status.severity.unknown}
                                                                </span>
                                                            )}
                                                            {asset.status.severity.info > 0 && (
                                                                <span style={{
                                                                    background: '#f8f9fa',
                                                                    color: '#2c3e50',
                                                                    padding: '0.25rem 0.5rem',
                                                                    borderRadius: '4px',
                                                                    fontSize: '0.875rem'
                                                                }}>
                                                                    Info: {asset.status.severity.info}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td style={{ padding: '1rem' }}>
                                                        <span style={{
                                                            background: '#f8f9fa',
                                                            padding: '0.25rem 0.5rem',
                                                            borderRadius: '4px',
                                                            fontSize: '0.875rem',
                                                            color: '#2c3e50'
                                                        }}>
                                                            {asset?.lastScanDate || asset?.activeVulnLastScanDate ? new Date(asset?.lastScanDate || asset?.activeVulnLastScanDate).toLocaleDateString() : 'Never scanned'}
                                                        </span>
                                                    </td>
                                                    <td style={{ padding: '1rem' }}>
                                                        <div style={{
                                                            display: 'flex',
                                                            gap: '0.5rem',
                                                            flexWrap: 'wrap',
                                                            alignItems: 'center'
                                                        }}>
                                                            <button
                                                                style={{
                                                                    background: '#453acd',
                                                                    color: '#fff',
                                                                    border: 'none',
                                                                    padding: '0.5rem 1rem',
                                                                    borderRadius: '6px',
                                                                    cursor: 'pointer',
                                                                    display: 'inline-flex',
                                                                    alignItems: 'center',
                                                                    gap: '0.5rem',
                                                                    transition: 'all 0.2s',
                                                                    opacity: (asset.scanStatus === 'pending') ? 0.7 : 1
                                                                }}
                                                                onClick={() => handleScan(asset._id, asset.value, asset.type)}
                                                                disabled={asset.scanStatus === 'pending' || asset.scanStatus === 'scanning'}
                                                            >
                                                                {asset.scanStatus === 'pending' ? (
                                                                    <>
                                                                        <i className="fa fa-clock"></i>
                                                                        Pending
                                                                    </>
                                                                ) : asset.scanStatus === 'scanning' ? (
                                                                    <>
                                                                        <i className="fa fa-spinner fa-spin"></i>
                                                                        Scanning
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <i className="fa fa-search"></i>
                                                                        Scan
                                                                    </>
                                                                )}
                                                            </button>
                                                            {asset.type === 'ip' && (
                                                                <button
                                                                    style={{
                                                                        background: '#dc3545',
                                                                        color: '#fff',
                                                                        border: 'none',
                                                                        padding: '0.5rem 1rem',
                                                                        borderRadius: '6px',
                                                                        cursor: 'pointer',
                                                                        display: 'inline-flex',
                                                                        alignItems: 'center',
                                                                        gap: '0.5rem',
                                                                        transition: 'all 0.2s',
                                                                        opacity: markingIncorrect[asset._id] ? 0.7 : 1
                                                                    }}
                                                                    onClick={() => handleMarkAsIncorrectIp(asset._id, asset.value)}
                                                                    disabled={markingIncorrect[asset._id]}
                                                                >
                                                                    {markingIncorrect[asset._id] ? (
                                                                        <>
                                                                            <i className="fa fa-spinner fa-spin"></i>
                                                                            Marking...
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <i className="fa fa-times-circle"></i>
                                                                            Mark as Incorrect IP
                                                                        </>
                                                                    )}
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td >
                                                </tr >
                                            ))}
                                            {
                                                assets.length === 0 && !loading && (
                                                    <tr>
                                                        <td colSpan="8" style={{
                                                            textAlign: 'center',
                                                            padding: '3rem'
                                                        }}>
                                                            <div style={{
                                                                color: '#6c757d'
                                                            }}>
                                                                <i className="fa fa-search fa-2x" style={{
                                                                    marginBottom: '1rem',
                                                                    display: 'block'
                                                                }}></i>
                                                                <p style={{ margin: 0 }}>No assets found</p>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )
                                            }
                                        </tbody >
                                    </table >
                                </div >
                            )}
                        </div >
                        <div

                        >
                            <div>
                                Showing {((pagination.currentPage - 1) * pagination.limit) + 1} to {Math.min(pagination.currentPage * pagination.limit, pagination.totalRecords)} of {pagination.totalRecords} entries
                            </div>
                            <div className="d-flex align-items-center">
                                {pagination.totalRecords > 0 && (
                                    <Pagination
                                        className="mb-0"
                                        currentPage={pagination.currentPage}
                                        totalCount={pagination.totalRecords}
                                        pageSize={pagination.limit}
                                        onPageChange={handlePageChange}
                                    />
                                )}
                            </div>
                        </div>
                    </div >
                </div >
            </div >

            {showAddTagModal && (
                <div className="modal" style={{ display: 'block' }}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    {selectedAssets.length > 0
                                        ? `Add Tags for ${selectedAssets.length} Selected Assets`
                                        : `Add Tags for ${selectedAsset?.value}`}
                                </h5>
                                <button
                                    type="button"
                                    className="close"
                                    onClick={() => {
                                        setShowAddTagModal(false);
                                        setNewTags([]);
                                        setNewTagInput('');
                                    }}
                                >
                                    <span>&times;</span>
                                </button>
                            </div>
                            <div className="modal-body">
                                {selectedAssets.length > 0 && (
                                    <div className="mb-3">
                                        <p style={{ margin: 0, color: '#6c757d' }}>
                                            Selected assets: {selectedAssets.length}
                                        </p>
                                        <div style={{
                                            marginTop: '0.5rem',
                                            maxHeight: '100px',
                                            overflowY: 'auto',
                                            padding: '0.5rem',
                                            background: '#f8f9fa',
                                            borderRadius: '4px'
                                        }}>
                                            {selectedAssets.map(asset => (
                                                <div key={asset._id} style={{
                                                    fontSize: '0.875rem',
                                                    color: '#2c3e50',
                                                    marginBottom: '0.25rem'
                                                }}>
                                                    {asset.value}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                <div className="mb-3">
                                    <label>Enter or select tag name:</label>
                                    <Select
                                        components={animatedComponents}
                                        options={[
                                            ...tagOptions.filter(option =>
                                                !newTags.includes(option.value)
                                            ),
                                            ...newTags.filter(tag => !availableTags.includes(tag)).map(tag => ({
                                                value: tag,
                                                label: tag,
                                                isNew: true
                                            }))
                                        ]}
                                        value={null}
                                        onChange={(selected) => {
                                            if (selected) {
                                                if (!newTags.includes(selected.value)) {
                                                    setNewTags([...newTags, selected.value]);
                                                }
                                            }
                                        }}
                                        onInputChange={(inputValue) => {
                                            setNewTagInput(inputValue);
                                        }}
                                        inputValue={newTagInput}
                                        placeholder="Type to search existing tags or enter new tag"
                                        isClearable
                                        isSearchable
                                        createOptionPosition="first"
                                        formatCreateLabel={(inputValue) => `Create "${inputValue}"`}
                                        onCreateOption={(inputValue) => {
                                            if (inputValue.trim() && !newTags.includes(inputValue.trim())) {
                                                setNewTags([...newTags, inputValue.trim()]);
                                                setNewTagInput('');
                                            }
                                        }}
                                        filterOption={(option, inputValue) => {
                                            if (!inputValue) return true;
                                            return option.label.toLowerCase().includes(inputValue.toLowerCase());
                                        }}
                                        styles={{
                                            control: (base) => ({
                                                ...base,
                                                border: '1px solid #ddd',
                                                borderRadius: '6px',
                                                boxShadow: 'none',
                                                '&:hover': {
                                                    borderColor: '#453acd'
                                                }
                                            }),
                                            option: (base, state) => ({
                                                ...base,
                                                backgroundColor: state.isFocused ? '#f8f9fa' : 'white',
                                                color: '#2c3e50'
                                            })
                                        }}
                                    />
                                </div>
                                <div className="mb-3">
                                    <label>Or type a new tag:</label>
                                    <div className="input-group">
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Type new tag and press Enter"
                                            value={newTagInput}
                                            onChange={(e) => setNewTagInput(e.target.value)}
                                            onKeyPress={(e) => {
                                                if (e.key === 'Enter' && newTagInput.trim()) {
                                                    e.preventDefault();
                                                    if (!newTags.includes(newTagInput.trim())) {
                                                        setNewTags([...newTags, newTagInput.trim()]);
                                                    }
                                                    setNewTagInput('');
                                                }
                                            }}
                                        />
                                        <div className="input-group-append">
                                            <button
                                                type="button"
                                                className="btn btn-outline-primary"
                                                onClick={() => {
                                                    if (newTagInput.trim() && !newTags.includes(newTagInput.trim())) {
                                                        setNewTags([...newTags, newTagInput.trim()]);
                                                        setNewTagInput('');
                                                    }
                                                }}
                                            >
                                                Add
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                <div className="mt-3">
                                    <label>Tags to be added:</label>
                                    <div style={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: '0.5rem',
                                        marginTop: '0.5rem'
                                    }}>
                                        {newTags.map(tag => (
                                            <span key={tag} style={{
                                                background: '#08283f',
                                                color: '#fff',
                                                padding: '0.25rem 0.5rem',
                                                borderRadius: '4px',
                                                fontSize: '0.875rem',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '0.25rem'
                                            }}>
                                                {tag}
                                                <i
                                                    className="fa fa-times"
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setNewTags(newTags.filter(t => t !== tag));
                                                    }}
                                                />
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => {
                                        setShowAddTagModal(false);
                                        setNewTags([]);
                                        setNewTagInput('');
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={() => {
                                        if (newTags.length > 0) {
                                            handleAddTag();
                                        }
                                    }}
                                    disabled={newTags.length === 0}
                                >
                                    Save Tags
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="modal-backdrop fade show"></div>
                </div>
            )}

            {
                showAddAssetModal && (
                    <div className="modal" style={{ display: 'block' }}>
                        <div className="modal-dialog">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">Add New Asset</h5>
                                    <button
                                        type="button"
                                        className="close"
                                        onClick={() => {
                                            setShowAddAssetModal(false);
                                            setAssetInput('');
                                            setAssetType('subdomain');
                                            setNewAssetTags([]);
                                            setNewAssetTagInput('');
                                        }}
                                    >
                                        <span>&times;</span>
                                    </button>
                                </div>
                                <div className="modal-body">
                                    <div className="form-group">
                                        <label>Asset Type</label>
                                        <select
                                            className="form-control"
                                            value={assetType}
                                            onChange={(e) => setAssetType(e.target.value)}
                                        >
                                            <option value="subdomain">Subdomain</option>
                                            <option value="ip">IP Address</option>
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label>Enter {assetType === 'subdomain' ? 'Subdomains' : 'IP Addresses'} (one per line)</label>
                                        <textarea
                                            className="form-control"
                                            rows="5"
                                            value={assetInput}
                                            onChange={(e) => setAssetInput(e.target.value)}
                                            placeholder={assetType === 'subdomain' ? 'example.com\nsub.example.com' : '1.1.1.1\n2.2.2.2'}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Add Tags (optional)</label>
                                        <Select
                                            components={animatedComponents}
                                            options={tagOptions.filter(option => !newAssetTags.includes(option.value))}
                                            value={null}
                                            onChange={(selected) => {
                                                if (selected && !newAssetTags.includes(selected.value)) {
                                                    setNewAssetTags([...newAssetTags, selected.value]);
                                                }
                                            }}
                                            onInputChange={(val) => setNewAssetTagInput(val)}
                                            inputValue={newAssetTagInput}
                                            placeholder="Select existing tag or type new tag"
                                            isClearable
                                            isSearchable
                                            styles={{
                                                control: (base) => ({
                                                    ...base,
                                                    border: '1px solid #ddd',
                                                    borderRadius: '6px',
                                                    boxShadow: 'none'
                                                })
                                            }}
                                        />
                                        <div className="input-group mt-2">
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="New tag"
                                                value={newAssetTagInput}
                                                onChange={(e) => setNewAssetTagInput(e.target.value)}
                                                onKeyPress={(e) => {
                                                    if (e.key === 'Enter' && newAssetTagInput.trim()) {
                                                        e.preventDefault();
                                                        if (!newAssetTags.includes(newAssetTagInput.trim())) {
                                                            setNewAssetTags([...newAssetTags, newAssetTagInput.trim()]);
                                                        }
                                                        setNewAssetTagInput('');
                                                    }
                                                }}
                                            />
                                            <div className="input-group-append">
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-primary"
                                                    onClick={() => {
                                                        if (newAssetTagInput.trim() && !newAssetTags.includes(newAssetTagInput.trim())) {
                                                            setNewAssetTags([...newAssetTags, newAssetTagInput.trim()]);
                                                        }
                                                        setNewAssetTagInput('');
                                                    }}
                                                >
                                                    Add Tag
                                                </button>
                                            </div>
                                        </div>
                                        {newAssetTags.length > 0 && (
                                            <div className="mt-2" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                                                {newAssetTags.map(tag => (
                                                    <span key={tag} style={{
                                                        background: '#453acd',
                                                        color: '#fff',
                                                        padding: '0.25rem 0.5rem',
                                                        borderRadius: '4px',
                                                        fontSize: '0.75rem',
                                                        display: 'inline-flex',
                                                        alignItems: 'center'
                                                    }}>
                                                        {tag}
                                                        <i
                                                            className="fa fa-times ml-1"
                                                            style={{ cursor: 'pointer', marginLeft: '0.25rem' }}
                                                            onClick={() => setNewAssetTags(newAssetTags.filter(t => t !== tag))}
                                                        />
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                        <small className="form-text text-muted">These tags will be assigned after assets are added.</small>
                                    </div>
                                    <div className="form-group">
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.5rem',
                                            marginTop: '1rem'
                                        }}>
                                            <input
                                                type="checkbox"
                                                id="activeScanCheckbox"
                                                checked={initiateActiveScan}
                                                onChange={(e) => setInitiateActiveScan(e.target.checked)}
                                                style={{
                                                    width: '18px',
                                                    height: '18px',
                                                    cursor: 'pointer',
                                                    margin: 0
                                                }}
                                            />
                                            <label
                                                htmlFor="activeScanCheckbox"
                                                style={{
                                                    margin: 0,
                                                    cursor: 'pointer',
                                                    fontSize: '0.95rem',
                                                    color: '#2c3e50',
                                                    userSelect: 'none'
                                                }}
                                            >
                                                Run active scan
                                            </label>
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={() => {
                                            setShowAddAssetModal(false);
                                            setAssetInput('');
                                            setAssetType('subdomain');
                                            setNewAssetTags([]);
                                            setNewAssetTagInput('');
                                        }}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        onClick={handleAddAsset}
                                        disabled={!assetInput.trim() || addingAsset}
                                    >
                                        {addingAsset ? (
                                            <>
                                                <div style={{
                                                    width: '1rem',
                                                    height: '1rem',
                                                    border: '2px solid #fff',
                                                    borderTop: '2px solid transparent',
                                                    borderRadius: '50%',
                                                    animation: 'spin 1s linear infinite',
                                                    marginRight: '0.5rem'
                                                }}></div>
                                                Adding...
                                            </>
                                        ) : (
                                            'Add Asset'
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div className="modal-backdrop fade show"></div>
                    </div>
                )
            }

            {
                showTagScanModal && (
                    <div className="modal" style={{ display: 'block' }}>
                        <div className="modal-dialog">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">Tag-Based Scan</h5>
                                    <button
                                        type="button"
                                        className="close"
                                        onClick={() => {
                                            setShowTagScanModal(false);
                                            setSelectedTagsForScan([]);
                                        }}
                                    >
                                        <span>&times;</span>
                                    </button>
                                </div>
                                <div className="modal-body">
                                    <div className="form-group">
                                        <label>Select Tags for Scanning</label>
                                        <Select
                                            components={animatedComponents}
                                            options={tagOptions}
                                            value={selectedTagsForScan.map(tag => ({ value: tag, label: tag }))}
                                            onChange={(selected) => {
                                                const selectedValues = selected ? selected.map(option => option.value) : [];
                                                setSelectedTagsForScan(selectedValues);
                                            }}
                                            placeholder="Select tags to scan"
                                            isMulti
                                            styles={{
                                                control: (base) => ({
                                                    ...base,
                                                    border: '1px solid #ddd',
                                                    borderRadius: '6px',
                                                    boxShadow: 'none',
                                                    '&:hover': {
                                                        borderColor: '#08283f'
                                                    }
                                                })
                                            }}
                                        />
                                        <small className="form-text text-muted">
                                            Select one or more tags. All assets with these tags will be scanned.
                                        </small>
                                    </div>
                                    {selectedTagsForScan.length > 0 && (
                                        <div className="mt-3">
                                            <label>Selected Tags:</label>
                                            <div style={{
                                                display: 'flex',
                                                flexWrap: 'wrap',
                                                gap: '0.5rem',
                                                marginTop: '0.5rem'
                                            }}>
                                                {selectedTagsForScan.map(tag => (
                                                    <span key={tag} style={{
                                                        background: '#28a745',
                                                        color: '#fff',
                                                        padding: '0.25rem 0.5rem',
                                                        borderRadius: '4px',
                                                        fontSize: '0.875rem',
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: '0.25rem'
                                                    }}>
                                                        {tag}
                                                        <i
                                                            className="fa fa-times"
                                                            style={{ cursor: 'pointer' }}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setSelectedTagsForScan(selectedTagsForScan.filter(t => t !== tag));
                                                            }}
                                                        />
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={() => {
                                            setShowTagScanModal(false);
                                            setSelectedTagsForScan([]);
                                        }}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-success"
                                        onClick={handleTagBasedScan}
                                        disabled={selectedTagsForScan.length === 0 || isTagScanning}
                                    >
                                        {isTagScanning ? (
                                            <>
                                                <div style={{
                                                    width: '1rem',
                                                    height: '1rem',
                                                    border: '2px solid #fff',
                                                    borderTop: '2px solid transparent',
                                                    borderRadius: '50%',
                                                    animation: 'spin 1s linear infinite',
                                                    marginRight: '0.5rem'
                                                }}></div>
                                                Initiating Scan...
                                            </>
                                        ) : (
                                            <>
                                                <i className="fa fa-search mr-2"></i>
                                                Start Scan
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div className="modal-backdrop fade show"></div>
                    </div>
                )
            }

            {/* Date Picker Popup */}
            {
                datePickerOpen && (
                    <div className="date-range-overlay" onClick={() => setDatePickerOpen(false)} style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 1050
                    }}>
                        <div className="date-range-picker-popup" onClick={(e) => e.stopPropagation()} style={{
                            backgroundColor: '#fff',
                            borderRadius: '8px',
                            padding: '20px',
                            width: '700px',
                            maxWidth: '90%',
                            boxShadow: '0 5px 15px rgba(0,0,0,0.3)'
                        }}>
                            <div className="date-range-picker-header" style={{
                                marginBottom: '15px',
                                fontWeight: 'bold',
                                fontSize: '16px',
                                borderBottom: '1px solid #eee',
                                paddingBottom: '10px'
                            }}>
                                {tempStartDate && tempEndDate
                                    ? `Selected date: ${format(tempStartDate, 'MM/dd/yyyy')} to ${format(tempEndDate, 'MM/dd/yyyy')}`
                                    : "Select date range"}
                            </div>
                            <div className="date-range-calendars" style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: '20px',
                                justifyContent: 'center'
                            }}>
                                <div>
                                    <h6>From</h6>
                                    <DatePicker
                                        selected={tempStartDate}
                                        onChange={(date) => {
                                            setTempStartDate(date);
                                            if (tempEndDate && date > tempEndDate) {
                                                setTempEndDate(null);
                                            }
                                        }}
                                        selectsStart
                                        startDate={tempStartDate}
                                        endDate={tempEndDate}
                                        inline
                                        showMonthDropdown
                                        showYearDropdown
                                        dropdownMode="select"
                                    />
                                </div>
                                <div>
                                    <h6>To</h6>
                                    <DatePicker
                                        selected={tempEndDate}
                                        onChange={(date) => setTempEndDate(date)}
                                        selectsEnd
                                        startDate={tempStartDate}
                                        endDate={tempEndDate}
                                        minDate={tempStartDate}
                                        inline
                                        showMonthDropdown
                                        showYearDropdown
                                        dropdownMode="select"
                                    />
                                </div>
                            </div>
                            <div className="date-range-actions" style={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                gap: '10px',
                                marginTop: '20px'
                            }}>
                                <button
                                    className="btn btn-primary"
                                    onClick={applyDateRange}
                                >
                                    Apply Filter
                                </button>
                                <button
                                    className="btn btn-secondary"
                                    onClick={cancelDateSelection}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }
        </div >
    );
};

export default ConsolidatedAssets;
